import {
    ButtonGradientPrimary,
    Colors,
    Container,
    FIXED_CIRCLE_DIMENSIONS,
    Heading,
    Text,
} from '@amelio/ui-library';
import { useMemo } from 'react';

import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';
import type { EmployeeCompetency } from '@/types/manager-assessment/Competency';

import { ProficiencyIcon } from '@/components/ProficiencyIcon/ProficiencyIcon';
import { PlanLockIcon, usePlanLock } from '@/components/ProGate/PlanLock';
import { PROFICIENCY_LEVEL_COLORS } from '@/constants/managerAssessment';
import { getIconEntry } from '@/constants/proficiencyIconCatalog';
import { useAmelioTranslations } from '@/hooks';
import { groupCompetenciesByName } from '@/utils/competencyGrouping';
import { getLocalizedText } from '@/utils/localization';
import { orderProficiencyLevelsWorstFirst } from '@/utils/proficiencyLevel';

const getQuickLevelColor = ({
    levelIndex,
    totalLevels,
}: {
    levelIndex: number;
    totalLevels: number;
}): string => {
    const lastPaletteIndex = PROFICIENCY_LEVEL_COLORS.length - 1;
    const spread = Math.round((levelIndex * lastPaletteIndex) / Math.max(totalLevels - 1, 1));
    return PROFICIENCY_LEVEL_COLORS[Math.min(spread, lastPaletteIndex)];
};

interface SelectedLevelIconProps {
    level: ProficiencyLevel;
}

const SelectedLevelIcon = ({ level }: SelectedLevelIconProps) => {
    const iconEntry = level.icon ? getIconEntry(level.icon) : undefined;
    const hasIcon = Boolean(level.iconUrl) || Boolean(iconEntry);

    if (!hasIcon) {
        return <span className={'h-[8px] w-[8px] rounded-full bg-white/50'} />;
    }

    return (
        <ProficiencyIcon
            iconUrl={level.iconUrl}
            iconEntry={iconEntry}
            color={Colors.WHITE}
            className={'h-[16px] w-[16px]'}
        />
    );
};

interface SwatchContentProps {
    isSelected: boolean;
    level: ProficiencyLevel;
}

const SwatchContent = ({ isSelected, level }: SwatchContentProps) => {
    if (isSelected) {
        return (
            <span className={'absolute inset-0 flex items-center justify-center'}>
                <SelectedLevelIcon level={level} />
            </span>
        );
    }

    return (
        <span
            className={
                'absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100'
            }
        >
            <span className={'h-[8px] w-[8px] rounded-full bg-white/50'} />
        </span>
    );
};

interface ProficiencyBoxesProps {
    itemId: string;
    levels: ProficiencyLevel[];
    selectedIndex: number | undefined;
    onSelect: (levelIndex: number) => void;
}

const ProficiencyBoxes = ({ itemId, levels, selectedIndex, onSelect }: ProficiencyBoxesProps) => {
    const { t } = useAmelioTranslations();

    const selectedLabel =
        selectedIndex !== undefined && levels[selectedIndex]
            ? getLocalizedText(levels[selectedIndex].label)
            : t('managerAssessmentEvaluation.quickAssessment.select');

    return (
        <div
            className={
                'flex flex-shrink-0 flex-col gap-[8px] max-sm:items-start sm:min-w-[140px] sm:items-end'
            }
        >
            <div
                className={
                    'flex items-center gap-[6px] rounded-[8px] border border-gray-200/50 bg-gray-100 p-[6px]'
                }
            >
                {levels.map((level, levelIndex) => {
                    const isSelected = selectedIndex === levelIndex;
                    const isFilled = selectedIndex !== undefined && levelIndex <= selectedIndex;

                    return (
                        <button
                            key={level.id ?? `level-${levelIndex}`}
                            type={'button'}
                            onClick={() => onSelect(levelIndex)}
                            aria-label={getLocalizedText(level.label)}
                            className={`group relative ${FIXED_CIRCLE_DIMENSIONS.md} rounded-[4px] transition-all duration-200 ${
                                isSelected
                                    ? 'scale-110 ring-2 ring-gray-200 ring-offset-1'
                                    : 'hover:scale-105'
                            } ${isFilled ? 'border-0' : 'border border-gray-200 bg-white'}`}
                            style={
                                isFilled
                                    ? {
                                          backgroundColor:
                                              level.iconColor ??
                                              getQuickLevelColor({
                                                  levelIndex,
                                                  totalLevels: levels.length,
                                              }),
                                      }
                                    : undefined
                            }
                            data-testid={`proficiency-box-${itemId}-${levelIndex}`}
                        >
                            <SwatchContent
                                isSelected={isSelected}
                                level={level}
                            />
                        </button>
                    );
                })}
            </div>

            <Text
                size={'small'}
                weight={'medium'}
                color={Colors.BASE_400}
                className={'text-[10px]'}
                data-testid={`proficiency-label-${itemId}`}
            >
                {selectedLabel}
            </Text>
        </div>
    );
};

interface QuickAssessmentGridProps {
    competencies: EmployeeCompetency[];
    proficiencyLevels: ProficiencyLevel[];
    assessments: Record<string, number>;
    onRate: (rating: { competencyId: string; levelIndex: number }) => void;
    onSubmit: () => void;
    /** A completed evaluation shows its ratings but takes no new ones. */
    readOnly?: boolean;
    dataTestId?: string;
}

export const QuickAssessmentGrid = ({
    competencies,
    proficiencyLevels,
    assessments,
    onRate,
    onSubmit,
    readOnly = false,
    dataTestId = 'quick-assessment-grid',
}: QuickAssessmentGridProps) => {
    const { t } = useAmelioTranslations();
    const planLock = usePlanLock();

    const groupedCompetencies = useMemo(
        () => groupCompetenciesByName(competencies, t('managerAssessmentSetup.ungrouped')),
        [competencies, t],
    );

    // Render the scale worst → best left-to-right (lowest proficiency first), matching the approved
    // Quick Assessment design. Canonical SortOrder 0 is still the best level; this only flips display.
    const orderedLevels = useMemo(
        () => orderProficiencyLevelsWorstFirst(proficiencyLevels),
        [proficiencyLevels],
    );

    const allCompetenciesRated =
        competencies.length > 0 &&
        competencies.every((competency) => assessments[competency.id] !== undefined);

    return (
        <div
            className={'flex flex-1 flex-col'}
            data-testid={dataTestId}
        >
            <div
                className={'flex flex-1 flex-col gap-[32px] pb-[24px]'}
                inert={readOnly}
                data-testid={`${dataTestId}-ratings`}
            >
                {groupedCompetencies.map((group) => (
                    <div
                        key={group.groupName}
                        className={'flex flex-col gap-[16px]'}
                        data-testid={`quick-assessment-group-${group.groupName}`}
                    >
                        <Heading
                            level={5}
                            weight={'semibold'}
                            className={'py-[8px] leading-normal'}
                        >
                            {group.groupName}
                        </Heading>

                        <div className={'flex flex-col gap-[12px]'}>
                            {group.competencies.map((competency) => (
                                <Container
                                    key={competency.id}
                                    gap={'sm'}
                                    borderRadius={'lg'}
                                    dataTestId={`quick-assessment-row-${competency.id}`}
                                    className={
                                        'items-start justify-between gap-[12px]! rounded-[8px]! border border-gray-200/50 bg-white/50 p-[12px] transition-colors hover:bg-white sm:flex-row sm:gap-[16px]! md:p-[16px]!'
                                    }
                                >
                                    <div className={'flex-1'}>
                                        <Text
                                            size={'small'}
                                            weight={'medium'}
                                            className={'mb-[4px] leading-normal'}
                                        >
                                            {competency.name}
                                        </Text>

                                        {competency.description && (
                                            <Text
                                                size={'small'}
                                                color={Colors.BASE_400}
                                                className={'text-[12px] leading-relaxed'}
                                                data-testid={`competency-description-${competency.id}`}
                                            >
                                                {competency.description}
                                            </Text>
                                        )}
                                    </div>

                                    <ProficiencyBoxes
                                        itemId={competency.id}
                                        levels={orderedLevels}
                                        selectedIndex={assessments[competency.id]}
                                        onSelect={(levelIndex) =>
                                            onRate({ competencyId: competency.id, levelIndex })
                                        }
                                    />
                                </Container>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {(!readOnly || planLock.isLocked) && (
                <div className={'flex justify-center border-t border-gray-200 pt-[24px]'}>
                    <ButtonGradientPrimary
                        size={'md'}
                        onClick={planLock.isLocked ? planLock.onLocked : onSubmit}
                        disabled={!planLock.isLocked && !allCompetenciesRated}
                        leftIcon={planLock.isLocked ? <PlanLockIcon /> : undefined}
                        dataTestId={'quick-assessment-submit'}
                    >
                        {t('managerAssessmentEvaluation.quickAssessment.submit')}
                    </ButtonGradientPrimary>
                </div>
            )}
        </div>
    );
};
